// node_modules
import {
    FC, useCallback, useContext, useEffect, useState, useRef
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp, faArrowDown, faTrashAlt, faPencilAlt, faSlash } from "@fortawesome/free-solid-svg-icons";
// Components
import { FindestRefTextBox } from "Components";
// Custom hooks
import { useDebounce } from "Hooks";
// Stores
import { CaseContext } from "Stores";
// Types
import { TTechnologyHighlightsGroupDTO } from "Types";
// Styles
import styles from "../technologyHighlights.module.scss";
// Controllers
import { TechnologyHighlightsControllerSingleton } from "Controllers";
// Constants
import { TechnologyHighlightsConstants } from "Constants";
// Contexts
import { WebsocketContext } from "Providers";
// Helpers
import { ToastHelperSingleton } from "Helpers";
// Enums
import { ToastTypeEnum } from "Enums";
// Constants
import { HubConstants } from "Constants";

type THighlightsGroupSidebarMenuItemProps = {
    highlightsGroup: TTechnologyHighlightsGroupDTO,
    highlightsGroupSize: number,
    itemIndexInSidebarMenu: number,
    selectedGroupId: number,
    setNewSelectedGroupId: (newSelectedGroupId: number) => void,
    coworkerTechnologyHighlightsGroups: TTechnologyHighlightsGroupDTO[],
    highlightsGroupNameResourceName: string,
    highlightsGroupsResourceName: string
};

export const HighlightsGroupSidebarMenuItem: FC<THighlightsGroupSidebarMenuItemProps> = (
    {
        highlightsGroup,
        highlightsGroupSize,
        itemIndexInSidebarMenu,
        selectedGroupId,
        setNewSelectedGroupId,
        coworkerTechnologyHighlightsGroups,
        highlightsGroupNameResourceName,
        highlightsGroupsResourceName
    }: THighlightsGroupSidebarMenuItemProps) => {
    // Ref
    const groupNameInputRef = useRef<HTMLInputElement>(null);

    // State
    const [currentGroupNameEdited, setCurrentGroupNameEdited] = useState<string | undefined>(undefined);
    const [currentGroupIdEdited, setCurrentGroupIdEdited] = useState<number | undefined>(undefined);
    const [isGroupNameInEditMode, setIsGroupNameInEditMode] = useState<boolean>(false);

    // Let
    const debouncedValue = useDebounce(currentGroupNameEdited, 500);

    // Contexts
    const { caseState } = useContext(CaseContext);

    // Get websocket context
    const { webSocketController } = useContext(WebsocketContext);

    // Logic
    useEffect(() => {
        if (!groupNameInputRef || !groupNameInputRef.current) return;
        groupNameInputRef.current.value = highlightsGroup.name;
    }, [highlightsGroup.name]);

    const editTechnologyHighlightsGroupName = useCallback(
        async (caseId: number, newTechnologyHighlightsGroupName: string) => {
            // safety-checks
            if (!currentGroupIdEdited || !newTechnologyHighlightsGroupName || !caseState.selectedTechnologyId || !caseId ||
                !currentGroupNameEdited) return;

            newTechnologyHighlightsGroupName = newTechnologyHighlightsGroupName.trim();

            setCurrentGroupNameEdited(undefined);
            await TechnologyHighlightsControllerSingleton.editTechnologyHighlightsGroupNameAsync(caseId, currentGroupIdEdited, newTechnologyHighlightsGroupName, caseState.selectedTechnologyId);
        }, [caseState.selectedTechnologyId, currentGroupIdEdited, currentGroupNameEdited]);

    useEffect(() => {
        // safety-checks
        if (!caseState.selectedCaseId) return;
        if (!debouncedValue) return;

        // send new name to the server
        editTechnologyHighlightsGroupName(caseState.selectedCaseId, debouncedValue);
    }, [debouncedValue, caseState.selectedCaseId, editTechnologyHighlightsGroupName]);

    const deleteTechnologyHighlightsGroup = async (technologyHighlightsGroupId: number): Promise<void> => {
        // safety-check
        if (!caseState.selectedCaseId || !caseState.selectedTechnologyId || !highlightsGroupsResourceName) return;

        // call API to delete technology highlights group
        await TechnologyHighlightsControllerSingleton
            .deleteTechnologyHighlightsGroupAsync(caseState.selectedCaseId, technologyHighlightsGroupId, caseState.selectedTechnologyId);
    };

    const moveTechnologyHighlightsGroupForward = async (technologyHighlightsGroupId: number): Promise<void> => {
        // safety-check
        if (!caseState.selectedCaseId || !caseState.selectedTechnologyId || !highlightsGroupsResourceName) return;

        // call API to move technology highlights group forward
        await TechnologyHighlightsControllerSingleton
            .moveTechnologyHighlightsGroupForwardAsync(caseState.selectedCaseId, caseState.selectedTechnologyId, technologyHighlightsGroupId);
    };

    const moveTechnologyHighlightsGroupBackwards = async (technologyHighlightsGroupId: number): Promise<void> => {
        // safety-check
        if (!caseState.selectedCaseId || !caseState.selectedTechnologyId || !highlightsGroupsResourceName) return;

        // call API to move technology highlights group backwards
        await TechnologyHighlightsControllerSingleton
            .moveTechnologyHighlightsGroupBackwardsAsync(caseState.selectedCaseId, caseState.selectedTechnologyId, technologyHighlightsGroupId);
    };

    const receivedEditStatusChange = (newIsEditingValue: boolean) => {
        setIsGroupNameInEditMode(newIsEditingValue);
    };

    const handleHighlightsGroupAlreadyBeingEdited = (): void => {
        ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Somebody is already editing this field.");
    };

    useEffect(() => {
        if (!highlightsGroupNameResourceName) return;

        webSocketController.addHandler(HubConstants.DELIVERY_RESOURCE_EDIT_STATUS_CHANGE_PREFIX + highlightsGroupNameResourceName, 
            receivedEditStatusChange);
        webSocketController.addHandler(HubConstants.DELIVERY_RESOURCE_ALREADY_BEING_EDITED_PREFIX + highlightsGroupNameResourceName,
            handleHighlightsGroupAlreadyBeingEdited);

        // on clean up time, send stop claiming edit message to prevent claiming lock 
        // and remove handlers
        return () => {
            webSocketController.invokeFunction(HubConstants.STOP_EDITING_DELIVERY_RESOURCE, highlightsGroupNameResourceName);
            webSocketController.removeHandler(HubConstants.DELIVERY_RESOURCE_EDIT_STATUS_CHANGE_PREFIX + highlightsGroupNameResourceName, 
                receivedEditStatusChange);
            webSocketController.removeHandler(HubConstants.DELIVERY_RESOURCE_ALREADY_BEING_EDITED_PREFIX + highlightsGroupNameResourceName,
                handleHighlightsGroupAlreadyBeingEdited);
        };
    }, [highlightsGroupNameResourceName, webSocketController]);

    const changeIsEditing = (doStartEditing: boolean) => {
        if (!highlightsGroupNameResourceName) return;

        if (doStartEditing) {
            webSocketController.invokeFunction(HubConstants.START_EDITING_DELIVERY_RESOURCE, highlightsGroupNameResourceName);
        } else {
            webSocketController.invokeFunction(HubConstants.STOP_EDITING_DELIVERY_RESOURCE, highlightsGroupNameResourceName);
        }
    };

    useEffect(() => {
        if (!groupNameInputRef || !groupNameInputRef.current || !isGroupNameInEditMode) return;
        groupNameInputRef.current.focus();
    }, [isGroupNameInEditMode]);

    return (
        <button className={`${highlightsGroup.id === selectedGroupId ? styles.active : ""} ${  styles.editableGroupName}`}
            onClick={() => { setNewSelectedGroupId(highlightsGroup.id); }}
        >
            <div style={{ display: (isGroupNameInEditMode && selectedGroupId === highlightsGroup.id) ? "block" : "none" }}>
                <FindestRefTextBox
                    ref={groupNameInputRef}
                    onClick={() => { setNewSelectedGroupId(highlightsGroup.id); }}
                    onChange={(newTechnologyHighlightsGroupName: string) => { setCurrentGroupNameEdited(newTechnologyHighlightsGroupName); setCurrentGroupIdEdited(highlightsGroup.id); }}
                    onBlur={() => { changeIsEditing(false); }}
                    hasNoPadding={true}
                />
            </div>
            <div
                style={{ display: isGroupNameInEditMode ? "none" : "block" }}
                className={styles.groupNameTitleContainer}>
                <span className={styles.groupNameTitle}>{highlightsGroup.name}</span>
                <span>{`(${highlightsGroupSize})`}</span>
            </div>
            <div className={styles.groupNameActionContainer}>
                {highlightsGroup.id !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_ID &&
                    highlightsGroup.name.toLowerCase() !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_NAME.toLowerCase() &&
                    !isGroupNameInEditMode ?
                    <div
                        className={[styles.changeContentButton, styles.edit].join(" ")}
                        title={"Edit group name"}
                        onClick={() => changeIsEditing(true)} >
                        <FontAwesomeIcon icon={faPencilAlt} />
                    </div>
                    :
                    null
                }
                {highlightsGroup.id !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_ID &&
                    highlightsGroup.name.toLowerCase() !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_NAME.toLowerCase() &&
                    isGroupNameInEditMode ?
                    <div
                        className={[styles.changeContentButton, styles.exitEdit].join(" ")}
                        title={"Exit edit mode"}
                        onClick={() => changeIsEditing(false)} >
                        <FontAwesomeIcon icon={faPencilAlt} />
                        <FontAwesomeIcon className={styles.slash} icon={faSlash} />
                        <FontAwesomeIcon className={styles.slashBottom} icon={faSlash} />
                    </div>
                    :
                    null
                }
                <div className={styles.moveGroupNameContainer}>
                    {highlightsGroup.id !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_ID &&
                        highlightsGroup.name.toLowerCase() !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_NAME.toLowerCase() &&
                        itemIndexInSidebarMenu !== 0 &&
                        itemIndexInSidebarMenu !== 1 ?
                        <div className={styles.moveGroupNameUp} title={"Move group up"} onClick={() => moveTechnologyHighlightsGroupBackwards(highlightsGroup.id)}>
                            <FontAwesomeIcon icon={faArrowUp} />
                        </div>
                        :
                        null
                    }
                    {highlightsGroup.id !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_ID &&
                        highlightsGroup.name.toLowerCase() !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_NAME.toLowerCase() &&
                        itemIndexInSidebarMenu + 1 !== coworkerTechnologyHighlightsGroups.length ?
                        <div className={styles.moveGroupNameDown} title={"Move group down"} onClick={() => moveTechnologyHighlightsGroupForward(highlightsGroup.id)}>
                            <FontAwesomeIcon icon={faArrowDown} />
                        </div>
                        :
                        null
                    }
                </div>
                {highlightsGroup.id !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_ID &&
                    highlightsGroup.name.toLowerCase() !== TechnologyHighlightsConstants.ALL_TECHNOLOGY_HIGHLIGHTS_GROUP_NAME.toLowerCase() ?
                    <div className={styles.deleteGroupName} title={"Delete group"} onClick={() => deleteTechnologyHighlightsGroup(highlightsGroup.id)}>
                        <FontAwesomeIcon icon={faTrashAlt} />
                    </div>
                    :
                    null
                }
            </div>
        </button>
    );
};
